import React from 'react';
import { Spinner, Text, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { CheckmarkCircleRegular, DismissCircleRegular } from '@fluentui/react-icons';

export type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

interface UploadStatusIndicatorProps {
    status: UploadStatus;
    fileCount?: number;
}

const useStyles = makeStyles({
    container: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        paddingTop: tokens.spacingVerticalS,
        paddingBottom: tokens.spacingVerticalS,
    },
    text: {
        fontSize: tokens.fontSizeBase200,
    },
    success: {
        color: tokens.colorStatusSuccessForeground1,
    },
    error: {
        color: tokens.colorStatusDangerForeground1,
    },
});

export const UploadStatusIndicator: React.FC<UploadStatusIndicatorProps> = ({ status, fileCount }) => {
    const classes = useStyles();

    if (status === 'idle') return null;

    if (status === 'uploading') {
        return (
            <div className={classes.container}>
                <Spinner size="tiny" />
                <Text className={classes.text}>
                    {fileCount ? `Uploading ${fileCount} file${fileCount > 1 ? 's' : ''}...` : 'Uploading...'}
                </Text>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div className={classes.container}>
                <CheckmarkCircleRegular className={classes.success} />
                <Text className={mergeClasses(classes.text, classes.success)}>Upload successful</Text>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <DismissCircleRegular className={classes.error} />
            <Text className={mergeClasses(classes.text, classes.error)}>Upload failed. Please try again.</Text>
        </div>
    );
};
